import React, { useEffect, useState, Fragment } from "react";
import { Row, Col, Card, Button, Modal, Form, Badge, Spinner, Table, InputGroup } from "react-bootstrap";
import { FaPlus, FaSearch, FaEdit, FaTrashAlt, FaFolderOpen, FaInfoCircle, FaShieldAlt } from "react-icons/fa";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import Select from "react-select";

const DocumentTypeMaster = () => {
 const [list, setList] = useState([]);
 const [filtered, setFiltered] = useState([]);
 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);
 const [roles, setRoles] = useState([]);

 const [showModal, setShowModal] = useState(false);
 const [editId, setEditId] = useState(null);
 const [search, setSearch] = useState("");

 const [form, setForm] = useState({
 name: "",
 description: "",
 status: "Active",
 allowedRoles: [],
 });

 /* =========================
 FETCH ROLES
 ========================= */
 const fetchRoles = async () => {
 try {
 const res = await api.get("/roles");
 if (res.data.success) {
 setRoles(res.data.data.map(r => ({ value: r.name.toLowerCase(), label: r.name })));
 }
 } catch (err) {
 console.error("Fetch Roles Error:", err);
 }
 };

 /* =========================
 FETCH DOCUMENT TYPES
 ========================= */
 const fetchTypes = async () => {
 setLoading(true);
 try {
 const res = await api.get("/document-types?includeInactive=true");
 if (res.data.success) {
 setList(res.data.data);
 setFiltered(res.data.data);
 }
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchTypes();
 fetchRoles();
 }, []);

 /* =========================
 SEARCH
 ========================= */
 useEffect(() => {
 if (!search.trim()) {
 setFiltered(list);
 } else {
 const s = search.toLowerCase();
 setFiltered(
 list.filter((i) => i.name.toLowerCase().includes(s))
 );
 }
 }, [search, list]);

 /* =========================
 MODAL HANDLERS
 ========================= */
 const openAdd = () => {
 setEditId(null);
 setForm({ name: "", description: "", status: "Active", allowedRoles: [] });
 setShowModal(true);
 };

 const openEdit = (item) => {
 setEditId(item._id);
 setForm({
 name: item.name,
 description: item.description || "",
 status: item.status,
 allowedRoles: item.allowedRoles || [],
 });
 setShowModal(true);
 };

 const closeModal = () => {
 if (!saving) setShowModal(false);
 };

 const handleChange = (e) => {
 setForm({ ...form, [e.target.name]: e.target.value });
 };

 /* =========================
 SAVE (CREATE / UPDATE)
 ========================= */
 const handleSave = async () => {
 if (!form.name.trim()) {
 return swal("Validation", "Name is required", "warning");
 }

 try {
 setSaving(true);
 if (editId) {
 await api.put(`/document-types/${editId}`, form);
 } else {
 await api.post("/document-types", form);
 }

 swal("Success", "Document type saved successfully", "success");
 closeModal();
 fetchTypes();
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Operation failed",
 "error"
 );
 } finally {
 setSaving(false);
 }
 };

 /* =========================
 DEACTIVATE
 ========================= */
 const deactivate = (item) => {
 swal({
 title: `Deactivate "${item.name}"?`,
 text: "This document type will no longer be available for uploads.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (ok) => {
 if (!ok) return;
 try {
 await api.delete(`/document-types/${item._id}`);
 fetchTypes();
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Cannot deactivate",
 "error"
 );
 }
 });
 };

 /* =========================
 RENDER
 ========================= */
 return (
 <Fragment>
 <PageTitle activeMenu="Document Type Master" motherMenu="Masters" />

 <Row>
 <Col lg={12}>
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white py-3 border-bottom-0">
 <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
 <div>
 <h4 className="card-title mb-0 fw-bold text-dark">
 <FaFolderOpen className="me-2 text-primary" />
 Document Types
 </h4>
 <p className="text-muted small mb-0 mt-1">
 Manage different document types and their access permissions
 </p>
 </div>
 <Button 
 variant="primary" 
 className="rounded-pill px-4 d-flex align-items-center gap-2"
 onClick={openAdd}
 >
 <FaPlus size={12} /> Add New Type
 </Button>
 </div>
 </Card.Header>

 <Card.Body className="pt-0">
 {/* SEARCH & FILTER BAR */}
 <div className="mb-4 premium-bg p-3 rounded-3">
 <Row className="align-items-center">
 <Col md={6} lg={4}>
 <InputGroup className="bg-white rounded-pill overflow-hidden border shadow-sm">
 <InputGroup.Text className="bg-white border-0 ps-3">
 <FaSearch className="text-muted" />
 </InputGroup.Text>
 <Form.Control
 className="border-0 shadow-none py-2"
 placeholder="Search document type name..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 </InputGroup>
 </Col>
 <Col md={6} lg={8} className="text-md-end mt-2 mt-md-0">
 <Badge bg="info-light" className="text-info border border-info px-3 py-2 rounded-pill">
 Total: {list.length} | Filtered: {filtered.length}
 </Badge>
 </Col>
 </Row>
 </div>

 {/* TABLE CONTAINER */}
 <div className="table-responsive">
 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" />
 <p className="mt-3 text-muted">Loading document types...</p>
 </div>
 ) : (
 <Table hover responsive className="align-middle custom-table mb-0">
 <thead className="premium-bg border-0 text-muted small text-uppercase fw-bold">
 <tr>
 <th className="py-3 ps-4">Name</th>
 <th className="py-3">Description</th>
 <th className="py-3">
 <FaShieldAlt className="me-1" /> Allowed Roles
 </th>
 <th className="py-3 text-center">Status</th>
 <th className="py-3 text-end pe-4">Actions</th>
 </tr>
 </thead>
 <tbody className="border-top-0">
 {filtered.map((item) => (
 <tr key={item._id} className="transition-all hover-bg-light">
 <td className="ps-4 fw-bold text-dark">{item.name}</td>
 <td className="text-muted small">
 {item.description ? (
 <div className="d-flex align-items-start gap-2 max-width-300">
 <FaInfoCircle className="mt-1 flex-shrink-0 opacity-50" />
 <span>{item.description}</span>
 </div>
 ) : (
 <span className="opacity-25 italic">-</span>
 )}
 </td>
 <td>
 {item.allowedRoles && item.allowedRoles.length > 0 ? (
 <div className="d-flex flex-wrap gap-1">
 {item.allowedRoles.map((role, idx) => (
 <Badge 
 key={idx} 
 bg="primary-light" 
 className="text-primary border border-primary px-2 py-1 small text-capitalize fw-medium"
 >
 {role}
 </Badge>
 ))}
 </div>
 ) : (
 <Badge bg="secondary-light" className="text-secondary border border-secondary px-2 py-1 small fw-medium">
 All Roles
 </Badge>
 )}
 </td>
 <td className="text-center">
 <Badge 
 bg={item.status === "Active" ? "success" : "danger"} 
 className={`rounded-pill px-3 py-1 ${item.status === "Active" ? "bg-success-light text-success" : "bg-danger-light text-danger"}`}
 >
 {item.status}
 </Badge>
 </td>
 <td className="text-end pe-4">
 <div className="d-flex justify-content-end gap-2">
 <Button
 size="sm"
 variant="outline-warning"
 className="btn-icon rounded-circle p-2 d-flex align-items-center justify-content-center shadow-none border-0"
 onClick={() => openEdit(item)}
 title="Edit Type"
 >
 <FaEdit size={16} />
 </Button>

 {item.status === "Active" && (
 <Button
 size="sm"
 variant="outline-danger"
 className="btn-icon rounded-circle p-2 d-flex align-items-center justify-content-center shadow-none border-0"
 onClick={() => deactivate(item)}
 title="Deactivate Type"
 >
 <FaTrashAlt size={16} />
 </Button>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </div>

 {!loading && filtered.length === 0 && (
 <div className="text-center py-5 premium-bg rounded-3 mt-3 border border-dashed">
 <div className="bg-white rounded-circle d-inline-flex p-4 shadow-sm mb-3">
 <FaFolderOpen size={40} className="text-muted opacity-25" />
 </div>
 <h5 className="text-dark fw-bold mb-1">No document types found</h5>
 <p className="text-muted small px-4">
 Try adjusting your search or add a new document type to get started.
 </p>
 <Button variant="outline-primary" size="sm" className="mt-2 rounded-pill px-4" onClick={openAdd}>
 Add New Type
 </Button>
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ================= MODAL ================= */}
 <Modal 
 show={showModal} 
 onHide={closeModal} 
 backdrop={saving ? "static" : true}
 centered
 className="document-type-modal"
 >
 <Modal.Header closeButton={!saving} className="border-0 px-4 pt-4 pb-0">
 <Modal.Title className="fw-bold fs-20">
 {editId ? "Update Document Type" : "Create Document Type"}
 </Modal.Title>
 </Modal.Header>

 <Modal.Body className="px-4 py-4">
 <Form>
 <Form.Group className="mb-4">
 <Form.Label className="text-dark fw-bold mb-2">Type Name <span className="text-danger">*</span></Form.Label>
 <Form.Control
 name="name"
 className="py-2 px-3 border-2"
 value={form.name}
 onChange={handleChange}
 placeholder="e.g. Work Order Document, Sale Invoice..."
 disabled={saving}
 autoFocus
 />
 </Form.Group>

 <Form.Group className="mb-4">
 <Form.Label className="text-dark fw-bold mb-2">Description</Form.Label>
 <Form.Control
 name="description"
 className="py-2 px-3 border-2"
 value={form.description}
 onChange={handleChange}
 as="textarea"
 rows={3}
 placeholder="Brief details about this document type..."
 disabled={saving}
 />
 </Form.Group>

 <Form.Group className="mb-4">
 <Form.Label className="text-dark fw-bold mb-2">
 <FaShieldAlt className="me-1 text-primary" /> 
 Allowed Roles
 </Form.Label>
 <Select
 isMulti
 options={roles}
 value={roles.filter(r => form.allowedRoles.includes(r.value))}
 onChange={(opts) => setForm({ ...form, allowedRoles: opts ? opts.map(o => o.value) : [] })}
 placeholder="Select roles who can see this..."
 disabled={saving}
 className="react-select-container"
 classNamePrefix="react-select"
 styles={{
 control: (base) => ({
 ...base,
 border: '2px solid #dee2e6',
 borderRadius: '8px',
 '&:hover': { border: '2px solid #886cff' }
 }),
 multiValue: (base) => ({
 ...base,
 backgroundColor: '#f0ecff',
 color: '#886cff',
 borderRadius: '6px'
 }),
 multiValueLabel: (base) => ({
 ...base,
 color: '#886cff',
 fontWeight: 'bold'
 })
 }}
 />
 <Form.Text className="text-muted small mt-2 d-block">
 <FaInfoCircle className="me-1" />
 Leave empty to allow <strong>all roles</strong> to access this document type.
 </Form.Text>
 </Form.Group>

 <Form.Group className="mb-2">
 <Form.Label className="text-dark fw-bold mb-2">Visibility Status</Form.Label>
 <div className="d-flex gap-4 mt-1">
 <Form.Check
 type="radio"
 id="status-active"
 name="status"
 label="Active"
 value="Active"
 checked={form.status === "Active"}
 onChange={handleChange}
 disabled={saving}
 className="status-check success"
 />
 <Form.Check
 type="radio"
 id="status-inactive"
 name="status"
 label="Inactive"
 value="Inactive"
 checked={form.status === "Inactive"}
 onChange={handleChange}
 disabled={saving}
 className="status-check danger"
 />
 </div>
 </Form.Group>
 </Form>
 </Modal.Body>

 <Modal.Footer className="border-0 px-4 pb-4 pt-0 gap-2">
 <Button 
 variant="light" 
 className="rounded-pill px-4 fw-bold" 
 onClick={closeModal} 
 disabled={saving}
 >
 Discard
 </Button>
 <Button 
 variant="primary" 
 className="rounded-pill px-4 fw-bold" 
 onClick={handleSave} 
 disabled={saving}
 >
 {saving ? (
 <>
 <Spinner size="sm" className="me-2" /> Saving...
 </>
 ) : (
 editId ? "Update Type" : "Create Type"
 )}
 </Button>
 </Modal.Footer>
 </Modal>

 {/* INJECT CUSTOM CSS */}
 <style>{`
 .hover-bg-light:hover { background-color: rgba(136, 108, 255, 0.04) !important; }
 .bg-primary-light { background-color: rgba(136, 108, 255, 0.1) !important; }
 .bg-success-light { background-color: rgba(40, 199, 111, 0.1) !important; }
 .bg-danger-light { background-color: rgba(234, 84, 85, 0.1) !important; }
 .bg-info-light { background-color: rgba(0, 207, 232, 0.1) !important; }
 .bg-secondary-light { background-color: rgba(130, 134, 139, 0.1) !important; }
 .transition-all { transition: all 0.2s ease-in-out; }
 .btn-icon:hover { background-color: #f8f9fa !important; transform: scale(1.1); }
 .max-width-300 { max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
 .status-check .form-check-input:checked { background-color: #886cff; border-color: #886cff; }
 .border-dashed { border: 2px dashed #dee2e6 !important; }
 `}</style>
 </Fragment>
 );
};

export default DocumentTypeMaster;
